<template>
  <div v-if="user">
    <div class="w-full text-center">
      <img crossorigin="anonymous" :src="pictureURL" class="inline object-cover rounded-full" :class="{
        'w-2/4 h-auto mb-4': avatarSize === 'big',
        'w-20 h-20': avatarSize === 'medium'
      }" />
    </div>
    <div class="w-full text-center mt-2 flex flex-col">
      <span class="font-bold text-black">{{ fullName }}</span>
      <span class="text-black" v-if="showEmail">{{ user.email }}</span>
      <span class="text-black inline-flex items-center justify-center">
        <span>{{ user.birthday || 'Fødselsdato ikke angivet' }}</span>
        <!--button type="button" class="profile-sync-btn" :class="{ syncing }" :disabled="syncing"
                title="Hent dine seneste oplysninger fra nyhedsbrevet"
                aria-label="Hent dine seneste oplysninger fra nyhedsbrevet" @click="syncFromMailchimp">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 12a8 8 0 0 1 13.66-5.66L20 8M20 8V3M20 8h-5M20 12a8 8 0 0 1-13.66 5.66L4 16M4 16v5M4 16h5"
                  stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button-->
      </span>
      <span v-if="syncMessage" class="text-sm" :class="syncError ? 'text-orange' : 'text-green'">{{ syncMessage }}</span>
      <a class="btn btn-link" href="#" @click.prevent="openModal">Opdater din profil</a>
      <span class="italic text-black my-8" v-if="showMemberSince">Medlem siden {{ user.createdAt }}</span>
    </div>

    <!-- Edit profile popup -->
    <transition name="fade">
      <div v-if="showModal" class="profile-modal-overlay" @click.self="closeModal">
        <div class="profile-modal" role="dialog" aria-modal="true">
          <div class="profile-modal-header">
            <h2 class="profile-modal-title">Opdater din profil</h2>
            <button type="button" class="profile-modal-close" aria-label="Luk" @click="closeModal">&times;</button>
          </div>

          <form @submit.prevent="submit">
            <div class="profile-form-group">
              <label for="profile-firstname">Fornavn</label>
              <input id="profile-firstname" type="text" v-model.trim="firstName" placeholder="Fornavn"
                     :class="{ 'is-invalid': submitted && !firstName }" />
              <div v-show="submitted && !firstName" class="invalid-feedback">Fornavn er påkrævet</div>
            </div>

            <div class="profile-form-group">
              <label for="profile-lastname">Efternavn</label>
              <input id="profile-lastname" type="text" v-model.trim="lastName" placeholder="Efternavn"
                     :class="{ 'is-invalid': submitted && !lastName }" />
              <div v-show="submitted && !lastName" class="invalid-feedback">Efternavn er påkrævet</div>
            </div>

            <div class="profile-form-group">
              <label for="profile-birthday">Fødselsdato</label>
              <input id="profile-birthday" type="date" v-model="birthday" :min="minBirthday" :max="maxBirthday"
                     :class="{ 'is-invalid': submitted && !isValidBirthday }" />
              <div v-show="submitted && !isValidBirthday" class="invalid-feedback">Indtast en gyldig fødselsdato (efter år 1900).</div>
            </div>

            <div v-if="error" class="text-center text-orange profile-error">{{ error }}</div>

            <div class="profile-modal-actions">
              <button type="button" class="btn-secondary" @click="closeModal" :disabled="saving">Annuller</button>
              <button type="submit" class="btn-primary" :disabled="saving">
                <span v-if="!saving">Gem</span>
                <span v-else>Gemmer..</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { APIClient } from '@/data/APIClient'
import { ProfileAction } from '@/data/ProfileAction'
import { User } from '@/data/models/User'

export default {
  name: 'UserInfo',
  props: {
    avatarSize: {
      type: String,
      required: false,
      default: 'big'
    },
    showAvatar: {
      type: Boolean,
      required: false,
      default: true
    },
    showName: {
      type: Boolean,
      required: false,
      default: true
    },
    showEmail: {
      type: Boolean,
      required: false,
      default: true
    },
    showMemberSince: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  data () {
    return {
      showModal: false,
      saving: false,
      submitted: false,
      error: null,
      firstName: '',
      lastName: '',
      birthday: '',
      syncing: false,
      syncMessage: '',
      syncError: false
    }
  },
  computed: {
    user () {
      return this.$store.state.user ?? null
    },
    // Prefer first + last name, fall back to the stored display name
    fullName () {
      if (!this.user) {
        return ''
      }

      const name = [this.user.firstName, this.user.lastName]
        .filter(part => part && part.length > 0)
        .join(' ')

      return name || this.user.name
    },
    pictureURL () {
      if (!this.$store.state.offline && this.user && this.user.image && this.user.image.length > 0) {
        return this.user.image
      }

      return '/img/default_profile.png'
    },
    // Earliest allowed birthday (start of year 1900)
    minBirthday () {
      return '1900-01-01'
    },
    // Latest allowed birthday (today — no future dates)
    maxBirthday () {
      const now = new Date()
      const y = now.getFullYear()
      const m = String(now.getMonth() + 1).padStart(2, '0')
      const d = String(now.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    },
    // Birthday is optional, but if provided it must be a real date in range
    isValidBirthday () {
      if (!this.birthday) {
        return true
      }

      const match = String(this.birthday).match(/^(\d{4})-(\d{2})-(\d{2})$/)
      if (!match) {
        return false
      }

      const year = parseInt(match[1], 10)
      return year >= 1900 && this.birthday >= this.minBirthday && this.birthday <= this.maxBirthday
    }
  },
  methods: {
    syncFromMailchimp () {
      if (this.syncing) {
        return
      }

      this.syncing = true
      this.syncMessage = ''
      this.syncError = false

      ProfileAction.sync()
        // Re-fetch the session so the view reflects the current backend state
        // (a fresh request avoids any stale in-request user/meta cache).
        .then(() => APIClient.client.get('/auth/info'))
        .then((response) => {
          this.$store.commit('user', User.fromAPI(response.data.user))
          localStorage.setItem('user', JSON.stringify(response.data.user))
          this.$store.commit('setOffline', false)
          this.syncError = false
          this.syncMessage = 'Din profil er opdateret.'
        })
        .catch((error) => {
          const message = error.response && error.response.data && error.response.data.message
          this.syncError = true
          this.syncMessage = message || 'Kunne ikke synkronisere. Prøv venligst igen.'
        })
        .finally(() => {
          this.syncing = false
        })
    },
    openModal () {
      if (!this.user) {
        return
      }

      // Prefill from the stored user, falling back to splitting the display name
      let firstName = this.user.firstName ?? ''
      let lastName = this.user.lastName ?? ''

      if (!firstName && !lastName && this.user.name) {
        const parts = this.user.name.trim().split(' ')
        firstName = parts.shift() ?? ''
        lastName = parts.join(' ')
      }

      this.firstName = firstName
      this.lastName = lastName
      this.birthday = this.normalizeDate(this.user.birthdayDate)
      this.error = null
      this.submitted = false
      this.showModal = true
    },
    /**
     * Coerce a stored birthday value into the strict YYYY-MM-DD format that
     * an <input type="date"> requires. Returns '' for anything unparseable.
     */
    normalizeDate (value) {
      if (!value) {
        return ''
      }

      // Already an ISO date (optionally with a time component)
      const isoMatch = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/)
      if (isoMatch) {
        return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`
      }

      // Fall back to native Date parsing (handles e.g. slash-separated dates)
      const parsed = new Date(value)
      if (!isNaN(parsed.getTime())) {
        const y = parsed.getFullYear()
        const m = String(parsed.getMonth() + 1).padStart(2, '0')
        const d = String(parsed.getDate()).padStart(2, '0')
        return `${y}-${m}-${d}`
      }

      return ''
    },
    closeModal () {
      if (this.saving) {
        return
      }
      this.showModal = false
    },
    submit () {
      this.submitted = true
      this.error = null

      if (!this.firstName || !this.lastName || !this.isValidBirthday) {
        return
      }

      this.saving = true
      ProfileAction.update(this.firstName, this.lastName, this.birthday)
        .then((response) => {
          const updatedUser = User.fromAPI(response.data.user)
          this.$store.commit('user', updatedUser)
          localStorage.setItem('user', JSON.stringify(response.data.user))
          this.showModal = false
        })
        .catch((error) => {
          const message = error.response && error.response.data && error.response.data.message
          this.error = message || 'Noget gik galt. Prøv venligst igen.'
        })
        .finally(() => {
          this.saving = false
        })
    }
  }
}
</script>

<style scoped lang="scss">
.profile-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
}

.profile-modal {
  background: #fff;
  border-radius: 16px;
  width: 100%;
  max-width: 360px;
  padding: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  text-align: left;
}

.profile-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.profile-modal-title {
  font-size: 1.25rem;
  font-weight: bold;
  color: #000;
}

.profile-modal-close {
  background: none;
  border: none;
  font-size: 1.75rem;
  line-height: 1;
  cursor: pointer;
  color: #888;
}

.profile-form-group {
  margin: 12px 0;
  display: flex;
  flex-direction: column;

  label {
    font-size: 0.85rem;
    margin-bottom: 4px;
    color: #333;
  }

  input {
    width: 100%;
    height: 44px;
    background: #e0dede;
    padding: 10px;
    border: none;
    outline: none;
    border-radius: 5px;

    &.is-invalid {
      border: 1px solid red;
    }
  }

  .invalid-feedback {
    color: red;
    font-size: 0.8rem;
    margin-top: 4px;
  }
}

.profile-error {
  color: #eea19e;
  margin: 8px 0;
}

.profile-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;

  button {
    border: none;
    border-radius: 5px;
    padding: 10px 22px;
    font-weight: bold;
    cursor: pointer;
    transition: .2s ease-in;

    &:disabled {
      opacity: 0.6;
      cursor: default;
    }
  }

  .btn-primary {
    color: #fff;
    background: #eea19e;

    &:hover:not(:disabled) {
      background: #E0967B;
    }
  }

  .btn-secondary {
    background: #e0dede;
    color: #333;
  }
}

.profile-sync-btn {
  background: none;
  border: none;
  padding: 0;
  margin-left: 8px;
  cursor: pointer;
  color: #6b7280;
  display: inline-flex;
  align-items: center;
  transition: color 0.2s;

  &:hover:not(:disabled),
  &:focus-visible {
    color: #111;
  }

  &:disabled {
    cursor: default;
    opacity: 0.7;
  }

  &.syncing svg {
    animation: profile-spin 0.8s linear infinite;
  }
  & svg > path {
    fill: transparent !important;
  }
}

@keyframes profile-spin {
  100% {
    transform: rotate(360deg);
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
